import type {
  CreateCustomJobDTO,
  CustomJobBoardQueryDTO,
  CustomJobListQueryDTO,
  UpdateCustomJobDTO,
} from "@/modules/custom-jobs/domain/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customJobsApi } from "./client";

const keys = {
  all: ["custom-jobs"] as const,
  board: (query?: CustomJobBoardQueryDTO) =>
    [...keys.all, "board", query] as const,
  list: (query?: CustomJobListQueryDTO) =>
    [...keys.all, "list", query] as const,
  detail: (id: string) => [...keys.all, "detail", id] as const,
};

function invalidateCustomJobs(qc: ReturnType<typeof useQueryClient>, id?: string) {
  qc.invalidateQueries({ queryKey: keys.all });
  if (id) qc.invalidateQueries({ queryKey: keys.detail(id) });
}

export function useCustomJobBoardQuery(
  query?: CustomJobBoardQueryDTO,
  enabled = true,
) {
  return useQuery({
    queryKey: keys.board(query),
    queryFn: () => customJobsApi.board(query),
    enabled,
  });
}

export function useCustomJobsQuery(
  query?: CustomJobListQueryDTO,
  enabled = true,
) {
  return useQuery({
    queryKey: keys.list(query),
    queryFn: () => customJobsApi.list(query),
    enabled,
  });
}

export function useCustomJobQuery(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => customJobsApi.get(id),
    enabled: Boolean(id),
  });
}

export function useCreateCustomJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomJobDTO) => customJobsApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useUpdateCustomJob(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCustomJobDTO) =>
      customJobsApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useConfirmCustomJobMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customJobsApi.confirm(id),
    onSuccess: (_, id) => invalidateCustomJobs(qc, id),
  });
}

export function useStartCustomJobMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customJobsApi.startWork(id),
    onSuccess: (_, id) => {
      invalidateCustomJobs(qc, id);
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useMarkReadyCustomJobMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customJobsApi.markReady(id),
    onSuccess: (_, id) => invalidateCustomJobs(qc, id),
  });
}

export function useCompleteCustomJobMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customJobsApi.complete(id),
    onSuccess: (_, id) => {
      invalidateCustomJobs(qc, id);
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useConfirmCustomJob(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => customJobsApi.confirm(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useStartCustomJob(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => customJobsApi.startWork(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useMarkReadyCustomJob(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => customJobsApi.markReady(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useCompleteCustomJob(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => customJobsApi.complete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useCancelCustomJob(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input?: { cancelNote?: string | null }) =>
      customJobsApi.cancel(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}
